import { Component, EventEmitter, Output } from '@angular/core';
import { toBrazilWallClockString } from '../../../utils/datetime.utils';

export interface ServiceOrderCloseData {
  /** yyyy-MM-dd */
  dataFechamento: string;
  /** HH:mm */
  horaFechamento: string;
}

export interface ServiceOrderCloseModalOptions {
  ordemServico?: string | null;
  statusAtual?: string | null;
  /** Data de abertura (yyyy-MM-dd) — o fechamento não pode ser anterior a ela. */
  dataAbertura?: string | null;
  /** Hora de abertura (HH:mm) usada quando o fechamento cai no mesmo dia da abertura. */
  horaAbertura?: string | null;
  /** True quando o modal foi oferecido pelo sistema após o serviço ser finalizado. */
  sugerido?: boolean;
}

@Component({
  selector: 'app-service-order-close-modal',
  templateUrl: './service-order-close-modal.component.html',
  styleUrls: ['./service-order-close-modal.component.css']
})
export class ServiceOrderCloseModalComponent {
  @Output() encerramentoConfirmado = new EventEmitter<ServiceOrderCloseData>();
  @Output() closeModal = new EventEmitter<void>();

  showModal: boolean = false;
  ordemServico: string = '';
  statusAtual: string = '';
  sugerido: boolean = false;

  dataFechamento: string = '';
  horaFechamento: string = '';

  private dataAbertura: string | null = null;
  private horaAbertura: string | null = null;

  open(options?: ServiceOrderCloseModalOptions): void {
    const agora = this.agoraBrasilia();

    this.ordemServico = (options?.ordemServico || '').toString().trim() || 'Não definida';
    this.statusAtual = (options?.statusAtual || '').toString().trim();
    this.sugerido = options?.sugerido === true;
    this.dataAbertura = this.normalizeDate(options?.dataAbertura);
    this.horaAbertura = this.normalizeTime(options?.horaAbertura);

    // Pré-preenchido com agora: fechar no momento atual continua sendo um clique.
    this.dataFechamento = agora.data;
    this.horaFechamento = agora.hora;
    this.showModal = true;
  }

  close(): void {
    this.showModal = false;
    this.closeModal.emit();
  }

  get maxData(): string {
    return this.agoraBrasilia().data;
  }

  get minData(): string {
    return this.dataAbertura || '';
  }

  /** Mensagem única de erro: null quando a data/hora informada pode ser gravada. */
  get erroValidacao(): string | null {
    const data = this.normalizeDate(this.dataFechamento);
    const hora = this.normalizeTime(this.horaFechamento);

    if (!data) return 'Informe a data do fechamento.';
    if (!hora) return 'Informe a hora do fechamento.';

    const agora = this.agoraBrasilia();
    if (this.compare(data, hora, agora.data, agora.hora) > 0) {
      return 'O fechamento não pode ser no futuro.';
    }

    if (this.dataAbertura && this.compare(data, hora, this.dataAbertura, this.horaAbertura || '00:00') < 0) {
      return `O fechamento não pode ser anterior à abertura da OS (${this.formatBr(this.dataAbertura)} ${this.horaAbertura || '00:00'}).`;
    }

    return null;
  }

  get isRetroativo(): boolean {
    const data = this.normalizeDate(this.dataFechamento);
    return !!data && data < this.agoraBrasilia().data;
  }

  confirmar(): void {
    if (this.erroValidacao) return;

    const data = this.normalizeDate(this.dataFechamento)!;
    const hora = this.normalizeTime(this.horaFechamento)!;

    this.showModal = false;
    this.encerramentoConfirmado.emit({ dataFechamento: data, horaFechamento: hora });
  }

  formatBr(dateValue?: string | null): string {
    const normalized = this.normalizeDate(dateValue);
    if (!normalized) return '';
    const [ano, mes, dia] = normalized.split('-');
    return `${dia}/${mes}/${ano}`;
  }

  private agoraBrasilia(): { data: string; hora: string } {
    const wall = toBrazilWallClockString(new Date());
    const [data, hora] = wall.split('T');
    return { data, hora: (hora || '00:00').slice(0, 5) };
  }

  private normalizeDate(value?: string | null): string | null {
    const raw = (value || '').toString().trim().split('T')[0];
    return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null;
  }

  private normalizeTime(value?: string | null): string | null {
    const match = /^(\d{1,2}):(\d{2})/.exec((value || '').toString().trim());
    if (!match) return null;

    const horas = Number(match[1]);
    const minutos = Number(match[2]);
    if (horas > 23 || minutos > 59) return null;

    return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}`;
  }

  private compare(dataA: string, horaA: string, dataB: string, horaB: string): number {
    return `${dataA}T${horaA}`.localeCompare(`${dataB}T${horaB}`);
  }
}
